import { useEffect, useMemo, useState } from "react";
import { CalendarIcon } from "@app/lib/events-admin-icons";
import { Button, RangeCalendar, cx } from "@app/lib/ui-foundation";
import { getLocalTimeZone, parseDate } from "@internationalized/date";
import { useDateFormatter } from "react-aria";
import {
  Button as AriaButton,
  DateRangePicker as AriaDateRangePicker,
  Group as AriaGroup,
  Popover as AriaPopover,
  Dialog as AriaPopoverDialog,
} from "react-aria-components";

// A non-modal Popover does not dismiss on outside pointer presses (react-aria
// sets isDismissable to !isNonModal), so close it manually while keeping page
// scroll unlocked.
function CloseOnOutsidePress({ onClose }: { onClose: () => void }) {
  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Element | null;
      if (!target) {
        return;
      }
      if (target.closest(".events-date-range-picker__popover")) {
        return;
      }
      if (target.closest(".events-date-range-picker")) {
        return;
      }
      onClose();
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, [onClose]);

  return null;
}

type EventDateRangePickerProps = {
  startDate: string;
  endDate: string;
  onChange: (nextStartDate: string, nextEndDate: string) => void;
};

export function EventDateRangePicker({
  startDate,
  endDate,
  onChange,
}: EventDateRangePickerProps) {
  const formatter = useDateFormatter({
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  const value = useMemo(() => {
    if (!startDate || !endDate) {
      return null;
    }

    return {
      start: parseDate(startDate),
      end: parseDate(endDate),
    };
  }, [endDate, startDate]);

  const [draftValue, setDraftValue] = useState(value);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setDraftValue(value);
  }, [value]);

  const formattedStartDate = value?.start
    ? formatter.format(value.start.toDate(getLocalTimeZone()))
    : "Select date";
  const formattedEndDate = value?.end
    ? formatter.format(value.end.toDate(getLocalTimeZone()))
    : "Select date";

  return (
    <AriaDateRangePicker
      aria-label="Event dates"
      className="events-date-range-picker"
      isOpen={isOpen}
      onOpenChange={(nextIsOpen) => {
        if (nextIsOpen) setIsOpen(true);
      }}
      shouldCloseOnSelect={false}
      value={draftValue}
      onChange={(nextValue) => {
        setDraftValue(nextValue);
      }}
    >
      <AriaGroup className="events-date-range-picker__group">
        <AriaButton className="events-date-range-picker__trigger">
          <div className="events-date-range-picker__segment">
            <span>Event Start*</span>
            <strong>{formattedStartDate}</strong>
          </div>
          <div className="events-date-range-picker__divider" />
          <div className="events-date-range-picker__segment">
            <span>Event End*</span>
            <strong>{formattedEndDate}</strong>
          </div>
          <span className="events-date-range-picker__icon" aria-hidden="true">
            <CalendarIcon className="size-4" />
          </span>
        </AriaButton>
      </AriaGroup>

      <AriaPopover
        isNonModal
        offset={10}
        placement="bottom"
        className={({ isEntering, isExiting }) =>
          cx(
            "fixed! top-1/2! bottom-auto! left-1/2! right-auto! z-50 flex max-h-[calc(100dvh-2rem)]! min-h-0 -translate-x-1/2! -translate-y-1/2! flex-col will-change-transform",
            isEntering && "duration-150 ease-out animate-in fade-in zoom-in-95",
            isExiting &&
              "duration-100 ease-in animate-out fade-out zoom-out-95",
          )
        }
      >
        <CloseOnOutsidePress onClose={() => setIsOpen(false)} />
        <AriaPopoverDialog className="events-date-range-picker__popover flex min-h-0 flex-col">
          {() => (
            <div
              className="contents"
              onKeyDown={(event) => {
                if (event.key === "Escape") setIsOpen(false);
              }}
            >
              <div className="events-date-range-picker__popover-header">
                <h4>Select Event Dates</h4>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto">
                <RangeCalendar />
              </div>

              <div className="events-date-range-picker__popover-footer">
                <Button
                  size="md"
                  color="secondary"
                  className="events-outline-button"
                  onPress={() => {
                    setDraftValue(value);
                    setIsOpen(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="md"
                  color="primary"
                  className="events-solid-button"
                  onPress={() => {
                    if (draftValue?.start && draftValue?.end) {
                      onChange(
                        draftValue.start.toString(),
                        draftValue.end.toString(),
                      );
                    }
                    setIsOpen(false);
                  }}
                >
                  Apply Dates
                </Button>
              </div>
            </div>
          )}
        </AriaPopoverDialog>
      </AriaPopover>
    </AriaDateRangePicker>
  );
}
